/**
 * PraticaDetailPage — F-006 Phase 5 (IMPL-P5-006).
 *
 * Route: `/app/archivio/:id`. Read-only detail view of a single pratica:
 *  - DatiPraticaCard         (anagrafica + immobile + contratto)
 *  - DocumentiSection        (4 DocumentoDownloadCard + manual "Aggiorna")
 *  - CronologiaStatiList     (timeline of the 4 milestones)
 *
 * Auth is enforced by `RequireAuth` and the layout comes from `AppLayout`.
 * Mounts its own QueryClientProvider (shared singleton from `query-client.ts`).
 */
import { Link, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireAuth } from '@/features/auth/components/RequireAuth';

import { archivioQueryClient } from '../lib/query-client';
import { usePraticaDetail } from '../hooks/usePraticaDetail';
import { AdempimentoCTA } from '@/features/pratiche-cliente/components/AdempimentoCTA';
import { DatiPraticaCard } from '../components/DatiPraticaCard';
import { DocumentiSection } from '../components/DocumentiSection';
import { CronologiaStatiList } from '../components/CronologiaStatiList';
import { isArchivioApiError } from '../lib/archivio-client';

function PraticaDetailInner({ id }: { id: string }) {
  const { data, error, isLoading, isFetching, isError, refresh } = usePraticaDetail(id);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center justify-center rounded-md border bg-muted/20 py-16 text-sm text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        Caricamento pratica…
      </div>
    );
  }

  if (isError || !data) {
    const isNotFound = isArchivioApiError(error) && error.status === 404;
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>
          {isNotFound ? 'Pratica non trovata' : 'Errore durante il caricamento'}
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            {isNotFound
              ? 'La pratica richiesta non esiste o non è accessibile.'
              : 'Si è verificato un problema nel caricamento della pratica.'}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to="/app/archivio">
                <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Torna all'archivio
              </Link>
            </Button>
            {!isNotFound ? (
              <Button type="button" variant="outline" size="sm" onClick={refresh}>
                Riprova
              </Button>
            ) : null}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Button variant="link" size="sm" asChild className="h-auto px-0 text-muted-foreground">
          <Link to="/app/archivio">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Archivio
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              Pratica {data.tipologia}
            </h1>
            <p className="text-sm text-muted-foreground">
              {data.proprietarioNome} / {data.inquilinoNome} — {data.indirizzoImmobile}
            </p>
          </div>
          {/* F-004: CTA incondizionata — le voci in archivio sono COMPLETATA per
              costruzione, quindi nessun guard di stato è necessario. */}
          <AdempimentoCTA praticaId={data.id} />
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <DatiPraticaCard pratica={data} />
          <DocumentiSection pratica={data} onRefresh={refresh} isRefreshing={isFetching} />
        </div>
        <aside>
          <CronologiaStatiList entries={data.cronologia} />
        </aside>
      </div>
    </div>
  );
}

export function PraticaDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <RequireAuth>
      <AppLayout>
        <QueryClientProvider client={archivioQueryClient}>
          {id ? (
            <PraticaDetailInner id={id} />
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>ID pratica mancante</AlertTitle>
              <AlertDescription>Torna all'archivio e seleziona una pratica.</AlertDescription>
            </Alert>
          )}
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}
